import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Megaphone, Package, Rocket, ChevronRight, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import CampagneFlowDetail from "@/pages/CampagneFlowDetail";
import { useAuth } from "@/context/AuthContext";
import { useDetailNav } from "@/lib/overzichtNav";
import { OverzichtNav } from "@/components/OverzichtNav";

const FASES = ["Cold", "Terugbellen", "Sample opsturen", "Sample opgestuurd", "Onderhandeling", "Deal!", "Afgevallen"];

const FASE_KANS = { "Cold": 0, "Terugbellen": 5, "Sample opsturen": 20, "Sample opgestuurd": 35, "Onderhandeling": 45, "Deal!": 100, "Afgevallen": 0 };

export default function CampagneDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nav = useDetailNav("campagnes", id, "/campagnes");
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";

  const [campagne, setCampagne] = useState(null);
  const [kansen, setKansen] = useState([]);
  const [orgs, setOrgs] = useState({});
  const [producten, setProducten] = useState({});

  const reload = async () => {
    try {
      const [{ data: c }, { data: o }, { data: p }] = await Promise.all([
        api.get(`/campagnes/${id}`),
        api.get("/organisaties"),
        api.get("/producten"),
      ]);
      setCampagne(c);
      setKansen(c.kansen || []);
      setOrgs(Object.fromEntries(o.map(x => [x.id, x.naam])));
      setProducten(Object.fromEntries(p.map(x => [x.id, x])));
    } catch {
      toast.error("Campagne niet gevonden");
      navigate("/campagnes");
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { setCampagne(null); }, [id]);

  const patch = async (field, value) => {
    try {
      const { data } = await api.patch(`/campagnes/${id}`, { [field]: value });
      setCampagne(prev => ({ ...prev, ...data, kansen: prev.kansen }));
      toast.success("Opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const remove = async () => {
    if (!window.confirm("Campagne verwijderen? De gekoppelde kansen blijven bestaan (campagne wordt ontkoppeld).")) return;
    await api.delete(`/campagnes/${id}`);
    toast.success("Verwijderd");
    navigate("/campagnes");
  };

  const totaalPerKans = useMemo(() => {
    const lijst = campagne?.product_lijst || [];
    return lijst.reduce((s, v) => {
      const p = producten[v.product_id];
      return s + (Number(v.aantal) || 0) * (Number(p?.verkoopprijs) || 0);
    }, 0) || Number(campagne?.template_bedrag || 0);
  }, [campagne, producten]);

  const pipeline = useMemo(() =>
    kansen.filter(k => k.fase !== "Afgevallen").reduce((s, k) => s + (k.bedrag || 0), 0)
  , [kansen]);
  const forecast = useMemo(() =>
    kansen.reduce((s, k) => s + (k.bedrag || 0) * (FASE_KANS[k.fase] || 0) / 100, 0)
  , [kansen]);
  const gewonnen = useMemo(() => kansen.filter(k => k.fase === "Deal!"), [kansen]);
  const afgevallen = useMemo(() => kansen.filter(k => k.fase === "Afgevallen").length, [kansen]);

  if (!campagne) return <div className="text-muted-foreground">Laden…</div>;
  if (campagne.type === "flow") return <CampagneFlowDetail campagne={campagne} />;

  return (
    <div className="space-y-6" data-testid="camp-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(nav.terug || "/campagnes")} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Overzicht
          </Button>
          <OverzichtNav nav={nav} basePad="/campagnes" testid="camp-nav" />
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={remove} className="text-destructive hover:text-destructive" data-testid="camp-delete">
            <Trash2 className="h-4 w-4 mr-1" />Verwijderen
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Linker: meta */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-4 h-fit">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            <Badge variant="outline">Campagne</Badge>
          </div>
          <Input
            value={campagne.naam || ""}
            onChange={(e)=>setCampagne({...campagne, naam: e.target.value})}
            onBlur={(e)=>patch("naam", e.target.value)}
            className="font-display text-2xl font-medium tracking-tight border-0 px-0 h-auto py-1 focus-visible:ring-0 focus-visible:border-0"
            data-testid="camp-naam"
          />
          <div>
            <Label className="text-xs text-muted-foreground">Omschrijving</Label>
            <Textarea value={campagne.omschrijving || ""}
              onChange={(e)=>setCampagne({...campagne, omschrijving: e.target.value})}
              onBlur={(e)=>patch("omschrijving", e.target.value)} rows={3} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Begin-fase</Label>
            <Select value={campagne.fase || "Cold"} onValueChange={(v)=>patch("fase", v)}>
              <SelectTrigger data-testid="camp-fase"><SelectValue /></SelectTrigger>
              <SelectContent>{FASES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="pt-3 border-t border-border space-y-2">
            <div className="text-xs uppercase text-muted-foreground tracking-wide flex items-center gap-1">
              <Package className="h-3 w-3" /> Artikelen ({(campagne.product_lijst || []).length})
            </div>
            {(campagne.product_lijst || []).length === 0 ? (
              <div className="text-sm text-muted-foreground italic">Geen artikelen — fallback {fmtEUR(campagne.template_bedrag || 0)} per kans.</div>
            ) : (
              <ul className="text-sm space-y-1">
                {campagne.product_lijst.map(v => {
                  const p = producten[v.product_id];
                  if (!p) return <li key={v.product_id} className="text-muted-foreground italic text-xs">— product verwijderd —</li>;
                  return (
                    <li key={v.product_id} className="flex justify-between gap-2">
                      <span className="truncate">{v.aantal}× {p.naam}</span>
                      <span className="text-muted-foreground whitespace-nowrap">{fmtEUR((v.aantal||0)*(p.verkoopprijs||0))}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="pt-2 border-t border-border/50 flex justify-between text-sm">
              <span className="text-muted-foreground">Totaal per kans</span>
              <strong>{fmtEUR(totaalPerKans)}</strong>
            </div>
          </div>

          <Button onClick={() => navigate(`/campagnes?tab=overzicht&launch=${id}`)} variant="outline" className="w-full" data-testid="open-launch">
            <Rocket className="h-4 w-4 mr-2" />Extra relaties toevoegen
          </Button>
        </div>

        {/* Rechter: kansen */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">Kansen</div>
              <div className="font-display text-2xl mt-1" data-testid="stat-kansen">{kansen.length}</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">Pipeline</div>
              <div className="font-display text-2xl mt-1">{fmtEUR(pipeline)}</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">Forecast</div>
              <div className="font-display text-2xl mt-1 text-primary">{fmtEUR(forecast)}</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">Deal! / Afgevallen</div>
              <div className="font-display text-2xl mt-1">{gewonnen.length} / {afgevallen}</div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border font-medium">Verkoopkansen in deze campagne</div>
            {kansen.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm">Nog geen kansen. Lanceer de campagne om kansen aan te maken.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Naam</th>
                    <th className="text-left p-3">Organisatie</th>
                    <th className="text-left p-3">Fase</th>
                    <th className="text-right p-3">Bedrag</th>
                    <th className="text-left p-3">Datum</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {kansen.map(k => (
                    <tr key={k.id} className="hover:bg-secondary/40 cursor-pointer transition-colors"
                        onClick={()=>navigate(`/verkoopkansen/${k.id}`)}
                        data-testid={`camp-kans-row-${k.id}`}>
                      <td className="p-3 font-medium">{k.naam || "(zonder naam)"}</td>
                      <td className="p-3" onClick={(e)=>e.stopPropagation()}>
                        <Link to={`/organisaties/${k.relatie_id}`} className="text-primary hover:underline">
                          {orgs[k.relatie_id] || "—"}
                        </Link>
                      </td>
                      <td className="p-3">
                        <Badge variant={k.fase === "Deal!" ? "default" : k.fase === "Afgevallen" ? "destructive" : "outline"}>
                          {k.fase}
                        </Badge>
                      </td>
                      <td className="p-3 text-right font-medium">{fmtEUR(k.bedrag)}</td>
                      <td className="p-3 text-muted-foreground">{fmtDate(k.datum)}</td>
                      <td className="p-3 text-muted-foreground"><ChevronRight className="h-4 w-4 inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
